import { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios'
import { getAPI } from '@umd-dit/oidc-core'

// Import workspace utilities directly from the component
import { getActiveWorkspace, getOrganizationId } from '@umd-dit/workspaces-component-react'

// Helper for getting selected workspace IDs - using our central utility
const getSelectedWorkspace = () => {
  const workspace = getActiveWorkspace()
  return {
    org_id: workspace?.org_id,
    ws_id: workspace?.ws_id
  }
}

// Optional request configuration extensions
interface RequestOptions extends AxiosRequestConfig {
  // Indicates this is a global endpoint (admin/system level)
  is_global?: boolean
  // Explicitly indicate this is a workspace-specific endpoint
  is_workspace_specific?: boolean
  // Request parameters
  params?: {
    org_id?: string;
    [key: string]: string | number | boolean | undefined;
  }
}

/**
 * Build a URL with the appropriate prefix based on the request type
 *
 * URL Structure Convention (Updated to match backend exactly):
 * 1. Workspace-specific routes: `/org/{org_id}/ws/{ws_id}/resource`
 * 2. Admin routes: `/admin/resource` (no app prefix needed)
 * 3. Job routes: `/jobs/resource` for global, `/org/{org_id}/ws/{ws_id}/jobs/resource` for workspace
 * 4. Datasource routes: `/datasource/{data_source}/bills/{session_code}`
 * 5. Special routes: `/health` and `/oidc/login` (no prefixes)
 */
export const buildUrl = (path: string, options: RequestOptions = {}): string => {
  const { is_global, is_workspace_specific } = options
  const cleanPath = path.startsWith('/') ? path : `/${path}`

  // Special routes like /health that shouldn't have any prefix
  if (path.startsWith('/health') || path.startsWith('/oidc/')) {
    return cleanPath;
  }

  // Handle legacy paths with /api/ prefix
  if (path.startsWith('/api/')) {
    return path.replace('/api', '');
  }

  // Handle global (admin) endpoints - all admin routes are direct /admin/* paths
  if (is_global) {
    return `/admin${cleanPath}`
  }

  // Handle workspace-specific endpoints - direct org/ws path
  if (is_workspace_specific) {
    const { org_id, ws_id } = getSelectedWorkspace()
    if (!org_id || !ws_id) {
      console.warn('Warning: workspace-specific request, but no workspace selected')
      return cleanPath
    }
    return `/org/${org_id}/ws/${ws_id}${cleanPath}`
  }

  // Handle job-related endpoints - no app prefix needed!
  if (path.startsWith('/jobs/')) {
    if (is_workspace_specific) {
      // Explicitly workspace-specific jobs: /org/{org_id}/ws/{ws_id}/jobs/*
      const { org_id, ws_id } = getSelectedWorkspace()
      return `/org/${org_id}/ws/${ws_id}${cleanPath}`
    } else {
      // Global jobs or explicitly non-workspace jobs: /jobs/* (direct path)
      return cleanPath
    }
  }

  // Handle datasource endpoints - direct path without prefixes
  if (path.startsWith('/datasource/')) {
    return cleanPath
  }

  // Get organization ID using the utility function
  const org_id = getOrganizationId(options.params?.org_id);

  // If path starts with /user, it's likely a user profile or workspace related route
  if (path.startsWith('/user/')) {
    return `/org/${org_id}${cleanPath}`;
  }

  // Default case - for any remaining endpoints that need direct access
  return cleanPath
}

class ApiClient {
  private getInstance(): AxiosInstance {
    const instance = getAPI('legislative');
    if (!instance) {
      throw new Error('Legislative API instance not initialized. Make sure createAPI("legislative", ...) is called in App.tsx');
    }
    return instance;
  }

  /**
   * Make a request with proper URL construction based on request type
   */
  public async request<T>(options: RequestOptions): Promise<AxiosResponse<T>> {
    const instance = this.getInstance();
    
    // Build the appropriate URL based on the request type
    if (options.url) {
      options.url = buildUrl(options.url, options)
    }

    // Add debug header if needed
    const isDebugMode = sessionStorage.getItem('debugModeEnabled') === 'true'
    if (isDebugMode && options.headers) {
      options.headers = {
        ...options.headers,
        'X-Debug-Mode': 'true'
      };
      console.log('[API] Adding X-Debug-Mode header')
    }

    // Log the request for debugging
    console.log(`[API] ${options.method || 'GET'} ${options.url}`)

    return instance.request<T>(options)
  }

  /**
   * GET request helper
   */
  public async get<T>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, method: 'GET' })
  }

  /**
   * POST request helper
   */
  public async post<T>(url: string, data?: unknown, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, data, method: 'POST' })
  }

  /**
   * PUT request helper
   */
  public async put<T>(url: string, data?: unknown, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, data, method: 'PUT' })
  }

  /**
   * DELETE request helper
   */
  public async delete<T>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, method: 'DELETE' })
  }
}

// Create a singleton instance that uses the centralized API management
export const apiClient = new ApiClient()